import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

const INTENTS = ["Find customers", "Find suppliers", "Find distributors", "Find partners", "Promote my business", "Discover businesses", "Network with business owners"];
const OFFERINGS = ["Products", "Services", "Manufacturing", "Wholesale", "Distribution", "Retail", "Other"];

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${active ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{children}</button>
);

export const OnboardingModal = ({ open, onOpenChange, business, onDone }) => {
  const [intents, setIntents] = useState(business?.intents || []);
  const [offerings, setOfferings] = useState(business?.offerings || []);
  const [products, setProducts] = useState((business?.products || []).join(", "));
  const [saving, setSaving] = useState(false);

  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/businesses/me", {
        intents, offerings,
        products: products.split(",").map((p) => p.trim()).filter(Boolean),
      });
      toast.success("Preferences saved — your discovery is now personalised!");
      onDone && onDone();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="onboarding-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2"><Sparkles className="text-brand" size={22} /> Personalise your circle</DialogTitle>
          <DialogDescription>Tell us what you're looking for so we can surface the right businesses.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 pt-2">
          <div>
            <p className="text-sm font-bold text-slate-700 mb-3">What are you looking for on IndiaBusinessCircle?</p>
            <div className="flex flex-wrap gap-2">{INTENTS.map((i) => <Chip key={i} testid={`onb-intent-${i}`} active={intents.includes(i)} onClick={() => toggle(intents, setIntents, i)}>{i}</Chip>)}</div>
          </div>
          <div>
            <p className="text-sm font-bold text-slate-700 mb-3">What does your business offer?</p>
            <div className="flex flex-wrap gap-2">{OFFERINGS.map((o) => <Chip key={o} testid={`onb-offering-${o}`} active={offerings.includes(o)} onClick={() => toggle(offerings, setOfferings, o)}>{o}</Chip>)}</div>
          </div>
          <div>
            <p className="text-sm font-bold text-slate-700 mb-2">Key products / services <span className="font-normal text-slate-400">(comma separated)</span></p>
            <input data-testid="onb-products" value={products} onChange={(e) => setProducts(e.target.value)} placeholder="e.g. Packaging, Cartons, Labels" className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm focus:border-brand focus:outline-none" />
          </div>
          <div className="flex gap-3">
            <Button data-testid="onb-skip" onClick={() => onOpenChange(false)} variant="ghost" className="flex-1 text-slate-500">Skip for now</Button>
            <Button data-testid="onb-save" onClick={save} disabled={saving} className="flex-1 h-11 bg-brand hover:bg-brand-hover font-semibold gap-2">{saving && <Loader2 className="animate-spin" size={16} />} Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
